<script setup>
import { ref, computed } from 'vue'
import NavItem from './NavItem.vue'
import moment from 'moment'

import { useAppointments } from '@store/appointments.js'
const $appointments = useAppointments()

import { useSettings } from '@store/settings.js'
const $settings = useSettings()

import { useCustomizer } from '@store/customizer.js'
const $customizer = useCustomizer()

// const $business = 

const showMore = ref(false)

const truncatedAbout = computed(() => {
    let text = $settings.addons.staffs ? $settings.customizer.sidebar.business_custom_about || $settings.business.about : $settings.customizer.sidebar.provider_bio
    if (!text) return ''

    if (showMore.value) return text

    // if text exceeds 76 characters
    if (text.length > 76) {
        return text.slice(0, 76) + '...'
    }

    // if text exceeds 3 lines
    if (String(text).split('\n').length > 3) {
        return String(text).split('\n').slice(0, 3).join('\n') + '...'
    }

    return text
})

// format time depending on customizer
const formatTime = (time, format) => {
    if ($settings.customizer.time.show_time) {
        return moment(time, format).format('HH:mm')
    } else {
        return moment(time, format).format('hh:mm A')
    }
}

const logo = computed(() => {
    if ($settings.customizer.sidebar.business_custom_logo !== '') return $settings.customizer.sidebar.business_custom_logo

    return $settings.business.full_logo_url
})

const backgroundTheme = computed(() => {
    if ($settings.customizer.sidebar.show_background == false) return

    if ($settings.customizer.sidebar.background_type == 'image') return `background-image: url(${$settings.customizer.sidebar.background_image});`
    if ($settings.customizer.sidebar.background_type == 'color') return `background-color: ${$settings.customizer.sidebar.background_color};`
})

const sidebarPosition = computed(() => {
    switch ($settings.customizer.sidebar.content_position) {
        case 'Left bottom':
            return 'justify-end'
        case 'Left top':
            return ''
        case 'Right bottom':
            return 'justify-end items-end'
        case 'Right top':
            return 'items-end'
    }
})

const isPositionRight = computed(() => {
    return $settings.customizer.sidebar.content_position.includes('Right')
})
</script>

<style lang="scss" scoped></style>

<template>
    <div class="sidenav-wrapper bg-center bg-no-repeat bg-cover flex flex-col" :class="sidebarPosition, {
        'rounded-l-lg border-l border-r-0': $customizer.isRounded && !$settings.isRTL,
        'rounded-r-lg border-l-0 border-r': $customizer.isRounded && $settings.isRTL,
        '!border-none': $settings.customizer.sidebar.show_background
    }" :style="backgroundTheme">
        <!-- company info -->
        <div class="company-logo-group w-[100px] max-h-full">
            <img v-if="logo" :src="logo" class="w-full max-h-full" />
            <img v-else :src="Image('prebook-logo-light.svg')" class="w-[61px] h-[50px]" />
            <!-- <img v-if="$settings.business.full_logo_url" :src="$settings.business.full_logo_url" width="90px" height="22px" class="logo-full" /> -->
        </div>

        <!-- for business profile -->
        <div v-if="$settings.addons.staffs">
            <div class="company-name" :style="{ color: $settings.customizer.sidebar.business_name_color }">{{ $settings.customizer.sidebar.business_custom_name || $settings.business.name }}</div>
            <div class="company-details whitespace-pre-line" :style="{ color: $settings.customizer.sidebar.business_about_color }">{{ truncatedAbout }} <span v-if="String($settings.customizer.sidebar.business_custom_about || $settings.business.name).length > 76 || String($settings.customizer.sidebar.business_custom_about || $settings.business.name).split('\n').length > 3" class="underline cursor-pointer" @click="showMore = !showMore">{{ showMore ? 'show less' : 'show more' }}</span></div>
        </div>

        <!-- for service provider profile -->
        <div v-if="!$settings.addons.staffs">
            <div class="company-name" :style="{ color: $settings.customizer.sidebar.provider_display_name_color }">{{ $settings.customizer.sidebar.provider_display_name || '' }}</div>
            <div class="company-details whitespace-pre-line" :style="{ color: $settings.customizer.sidebar.provider_bio_color }">{{ truncatedAbout }} <span v-if="String($settings.customizer.sidebar.provider_bio).length > 76 || String($settings.customizer.sidebar.provider_bio).split('\n').length > 3" class="underline cursor-pointer" @click="showMore = !showMore">{{ showMore ? 'show less' : 'show more' }}</span></div>
        </div>

        <!-- menu -->
        <div class="menu-items-wrapper" :class="{ 'justify-end': isPositionRight }">
            <NavItem v-if="$appointments.selectedService.service_id" link="service" icon="star04" :title="$appointments.selectedService.name" />
            <NavItem v-if="$appointments.selectedStaff.staff_id" link="staff" icon="cube01" :title="$appointments.selectedStaff.name" />
            <NavItem v-if="$appointments.selectedDate" link="date" icon="calendar" :title="`${$appointments.selectedDate.date} ${$appointments.selectedDate.month.split('').splice(0, 3).join('')} ${$appointments.selectedDate.year}`" />
            <NavItem v-if="$appointments.selectedTime.booking_time_start && $appointments.selectedTime.booking_time_end" link="time" icon="clock" :title="`${formatTime($appointments.selectedTime.booking_time_start, 'HH:mm')} - ${formatTime($appointments.selectedTime.booking_time_end, 'HH:mm')}`" />
        </div>
    </div>
</template>